import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, GitCompareArrows, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState, PillTabs } from '../../../components/design-system/admin';
import { SelectionBar } from '../../../components/design-system/common';
import { NetworkMatch, Paged, useNetworkApi } from '../../../common/hooks/use-network-api.hook';
import type { NetworkLinkStatus } from '../../../common/models/network.model';
import { readApiError } from '../../../common/utils/api-error';
import { MatchCard } from './MatchCard';

const PAGE_SIZE = 20;

/**
 * The matches waiting for a yes or a no, and the ones already decided.
 *
 * A decision takes the card out of the list at once (and with it the other
 * suggestions for the same product, which the API rejected in the same
 * breath), so the retailer works down a queue rather than re-reading cards
 * they have answered.
 */
export const MatchesPanel: React.FC<{
    suggestedCount: number;
    onChanged: () => void;
    onBrowse: () => void;
}> = ({ suggestedCount, onChanged, onBrowse }) => {
    const { t } = useTranslation();
    const api = useNetworkApi();
    const [status, setStatus] = useState<NetworkLinkStatus>('suggested');
    const [page, setPage] = useState(1);
    const [data, setData] = useState<Paged<NetworkMatch> | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState<Set<string>>(new Set());
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [bulkBusy, setBulkBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setData(await api.matches(status, page, PAGE_SIZE));
        } catch (e) {
            const body = await readApiError(e);
            setError(body.message || t('NETWORK_RETAILER.ERROR'));
        } finally {
            setLoading(false);
        }
    }, [api, status, page, t]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        setSelected(new Set());
    }, [status, page]);

    const drop = (ids: string[]) => {
        setData((d) => (d ? { ...d, items: d.items.filter((m) => !ids.includes(m.id)), total: Math.max(0, d.total - ids.length) } : d));
        setSelected((s) => new Set([...s].filter((id) => !ids.includes(id))));
    };

    const decide = async (match: NetworkMatch, decision: 'confirmed' | 'rejected') => {
        setBusy((b) => new Set(b).add(match.id));
        try {
            const outcome = await api.decide(match.id, decision);
            drop([match.id, ...(outcome.superseded || [])]);
            toast.success(t(decision === 'confirmed' ? 'NETWORK_RETAILER.MATCHES.TOAST_CONFIRMED' : 'NETWORK_RETAILER.MATCHES.TOAST_REJECTED'));
            onChanged();
        } catch (e) {
            const body = await readApiError(e);
            toast.error(body.message || t('NETWORK_RETAILER.ERROR'));
        } finally {
            setBusy((b) => {
                const next = new Set(b);
                next.delete(match.id);
                return next;
            });
        }
    };

    const confirmSelected = async () => {
        const ids = [...selected];
        if (!ids.length) return;
        setBulkBusy(true);
        try {
            const { results, decided } = await api.decideMany(ids, 'confirmed');
            const done = results.filter((r) => r.ok).flatMap((r) => [r.id, ...(r.superseded || [])]);
            const notApplied = results.filter((r) => !r.ok).length;
            toast.success(
                notApplied
                    ? t('NETWORK_RETAILER.MATCHES.BULK_PARTIAL', { count: decided, skipped: notApplied })
                    : t('NETWORK_RETAILER.MATCHES.BULK_DONE', { count: decided }),
            );
            // The ones not applied lost to a better match for the same product: reload to show the truth.
            if (notApplied) await load();
            else drop(done);
            onChanged();
        } catch (e) {
            const body = await readApiError(e);
            toast.error(body.message || t('NETWORK_RETAILER.ERROR'));
        } finally {
            setBulkBusy(false);
        }
    };

    const items = data?.items || [];
    const selectable = status === 'suggested';
    const allSelected = items.length > 0 && items.every((m) => selected.has(m.id));
    const someSelected = items.some((m) => selected.has(m.id));

    return (
        <div className="flex flex-col gap-4" data-testid="network-matches">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <PillTabs<NetworkLinkStatus>
                    value={status}
                    onChange={(v) => {
                        setStatus(v);
                        setPage(1);
                    }}
                    ariaLabel={t('NETWORK_RETAILER.MATCHES.TITLE')}
                    options={[
                        { value: 'suggested', label: t('NETWORK_RETAILER.MATCHES.TAB_SUGGESTED'), count: suggestedCount },
                        { value: 'confirmed', label: t('NETWORK_RETAILER.MATCHES.TAB_CONFIRMED') },
                        { value: 'rejected', label: t('NETWORK_RETAILER.MATCHES.TAB_REJECTED') },
                    ]}
                />
                {selectable && items.length > 0 && (
                    <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
                        <Checkbox
                            checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                            onCheckedChange={(v) => setSelected(v === true ? new Set(items.map((m) => m.id)) : new Set())}
                            data-testid="network-matches-select-all"
                        />
                        {t('NETWORK_RETAILER.MATCHES.SELECT_PAGE')}
                    </label>
                )}
            </div>

            {loading && !data && (
                <div className="flex flex-col gap-3" data-testid="network-matches-loading">
                    {[0, 1, 2].map((i) => (
                        <Skeleton key={i} className="h-36 rounded-xl" />
                    ))}
                </div>
            )}

            {error && (
                <div className="rounded-xl border border-destructive/30 bg-card p-4 text-sm text-destructive" data-testid="network-matches-error">
                    {error}
                    <Button variant="outline" size="sm" className="ms-3" onClick={load}>
                        {t('NETWORK_RETAILER.RETRY')}
                    </Button>
                </div>
            )}

            {!error && data && items.length === 0 && (
                <div className="rounded-xl border bg-card" data-testid="network-matches-empty">
                    <EmptyState
                        icon={<GitCompareArrows />}
                        title={t(`NETWORK_RETAILER.MATCHES.EMPTY_${status.toUpperCase()}`)}
                        description={t(`NETWORK_RETAILER.MATCHES.EMPTY_${status.toUpperCase()}_HINT`)}
                        action={
                            status === 'suggested' ? (
                                <Button variant="outline" onClick={onBrowse}>
                                    {t('NETWORK_RETAILER.MATCHES.EMPTY_BROWSE')}
                                </Button>
                            ) : undefined
                        }
                    />
                </div>
            )}

            {items.map((m) => (
                <MatchCard
                    key={m.id}
                    match={m}
                    busy={busy.has(m.id) || bulkBusy}
                    selected={selected.has(m.id)}
                    onSelect={selectable ? (v) => setSelected((s) => {
                        const next = new Set(s);
                        if (v) next.add(m.id);
                        else next.delete(m.id);
                        return next;
                    }) : undefined}
                    onDecide={(d) => decide(m, d)}
                />
            ))}

            {data && data.total > PAGE_SIZE && (
                <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
                    <span className="tabular-nums">
                        {t('NETWORK_RETAILER.PAGINATION', { from: (page - 1) * PAGE_SIZE + 1, to: Math.min(page * PAGE_SIZE, data.total), total: data.total })}
                    </span>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>
                            {t('NETWORK_RETAILER.PREVIOUS')}
                        </Button>
                        <Button variant="outline" size="sm" disabled={!data.nextPage || loading} onClick={() => setPage((p) => p + 1)}>
                            {t('NETWORK_RETAILER.NEXT')}
                        </Button>
                    </div>
                </div>
            )}

            <SelectionBar open={selectable && selected.size > 0} label={t('NETWORK_RETAILER.SELECTED', { count: selected.size })} testId="network-matches-bar">
                <Button variant="outline" size="sm" onClick={() => setSelected(new Set())} disabled={bulkBusy}>
                    {t('NETWORK_RETAILER.CLEAR_SELECTION')}
                </Button>
                <Button variant="outline" size="sm" onClick={confirmSelected} disabled={bulkBusy} data-testid="network-matches-confirm-selected">
                    {bulkBusy ? <Loader2 className="animate-spin" data-icon="inline-start" /> : <Check data-icon="inline-start" />}
                    {t('NETWORK_RETAILER.MATCHES.CONFIRM_SELECTED', { count: selected.size })}
                </Button>
            </SelectionBar>
        </div>
    );
};
